import React from 'react';
import {useSelector} from 'react-redux';
import {Platform, TouchableOpacity, View, Text} from 'react-native';
import createBottomTabNavigator from '../../shared/components/tabBar/navigator/createBottomTabNavigator';
import {useModal} from '../../shared/components/modal';
import {StackScreenProps} from '@react-navigation/stack';
import {createStackNavigator} from './stack';

import {CardServicesScreen} from '../../screens/cardServices';
import {SocialSupportMeasuresScreen} from '../../screens/socialSupportMeasures';
import {SupportServiceScreen} from '../../screens/supportService';
import {
  profileTokenSelector,
  profileCurrentRequestSelector,
  profileNotificationsByIdSelector,
  holderProfileSelector,
} from '../../redux/selectors/profile';
import {PollsMainScreen} from '../../screens/polls/main';
import {TransportServicesScreen} from '../../screens/transportService';
import {BankServicesScreen} from '../../screens/bankService';
import {PrivilegesScreen} from '../../screens/privileges';
import {StateProgramsScreen} from '../../screens/statePrograms';
import {StateAssignmentsScreen} from '../../screens/stateAssignments';
import {ExpenseLiabilitiesScreen} from '../../screens/expenseLiabilities';
import MainScreen from '../../screens/main';
import {PrivilegeDetailsScreen} from '../../screens/privilegesDetails';
import {VisuallyImpairedSettingsScreen} from '../../screens/visuallyImpairedSettings';
import {SupportServiceTopics} from '../../screens/supportService/components/topics';
import {PrivilegesResolver} from '../resolvers/privilegesResolver';
import {AboutApplicationScreen} from '../../screens/aboutApplication';

import {images} from '../../assets/icons';
import {useTheme} from '../theme';
import {useStyles} from '../hooks/styles';
import {Routes, Screens} from './index';
import {TabBarIconProps} from './model';
import {createChildScreenOptions, createRootScreenOptions} from './topBar';
import {SupportServiceTopic} from '../../screens/supportService/components/topic';
import {CreateRequest} from '../../screens/supportService/components/createRequest';
import {WebScreen} from '../../screens/webView';
import {defaults} from '../constants/defaults';
import {dateFormats} from '../constants/date';
import {NotificationsScreen} from '../../screens/notifications';
import {CardCodeScreen} from '../../screens/cardCode';
import {NotificationMobile} from '../../screens/notification/mobile';
import {PollsTypeSurveyScreen} from '../../screens/polls/surveys';
import {PollsTypeVoteScreen} from '../../screens/polls/votes';
import {PollsResolver} from '../resolvers/pollsResolver';
import {datePipe} from '../pipes/date';
import {PollsTypeSurveyFormScreen} from '../../screens/polls/surveyForm';
import {PollsTypeVoteFormScreen} from '../../screens/polls/voteForm';
import {PollDetailsResolver} from '../resolvers/pollDetailsResolver';
import {PollDetailsTypeResolver} from '../resolvers/pollDetailsTypeResolver';
import {TabBarCustom} from '../components/tabBar';
import {hexToRgbA} from '../pipes/color';
import {GradientView} from '../components/gradientView';
import {DrawerActions, getFocusedRouteNameFromRoute} from '@react-navigation/native';
import {MobileProfileNavigation} from './profile';
import {NewsScreen} from '../../screens/news';
import {NewsArticleScreen} from '../../screens/newsArticle';
import {LoyaltyProgramScreen} from '../../screens/loyaltyProgram';
import {GetHolderProfileCabinetResponsePayload} from '../models/api';
import {ChangeCityMobileScreen} from '../../screens/changeCity';
import {LoyaltyProgramAcceptorScreen} from '../../screens/loyaltyProgramAcceptor';
import {acceptorsFilterScreen} from '../../screens/acceptorsFilter';
import {useDimensions} from '../hooks/useDimensions';
import {GoToApp} from '../components/gotoApp';
import {CardStatus} from '../../types/enums/cardStatus';
import esiaActivateCard from '../../screens/login/esiaActivateCard/esiaActivateCard';
import {ProfileLinkedAccounts} from '../../screens/profile/mobile/components/linkedAccounts/view';
import {ProfileLinkedAccountsItem} from '../../screens/profile/mobile/components/linkedAccounts/item/view';
import esiaActivateCardAfterLoggedOnMobile from '../../screens/login/esiaActivateCard/esiaActivateCardAfterLoggedOnMobile/esiaActivateCardAfterLoggedOnMobile';
import {VolunteersServiceScreen} from '../../screens/volunteersService';
import {VolunteersServiceExchangeHistoryScreen} from '../../screens/volunteersService/components/volunteersProgram/VolunteersServiesExchangeHistory';
import {VolunteersServiceEventsScreen} from '../../screens/volunteersServiceEvents';
import {RewardShop} from '../../screens/rewardShop/RewardShop';
import {ProductItemScreen} from '../../screens/rewardShop/ProductPage';
import {FiltersViewMobile} from '../../screens/rewardShop/FiltersViewMobile/FiltersViewMobile.native';
import {CashBackProgramScreen} from '../../screens/cashBackProgram';
import {SmsEmailSubscriptionControlScreen} from '../../screens/smsEmailSubscriptionControl';
import {ECertificatesScreen} from '../../screens/eCertificates';
import {CertificateItemScreen} from '../../screens/certificateItem';

const Tabs = createBottomTabNavigator();
const MobileStackNavigation = createStackNavigator();
const cardServiceStack = createStackNavigator();
const pollsServiceStack = createStackNavigator();
const notificationsStack = createStackNavigator();
const mainStack = createStackNavigator();
const visuallyImpairedSettingsStack = createStackNavigator();
const newsStack = createStackNavigator();

export const Main = (props: StackScreenProps<any>) => {
  const theme = useTheme();
  const id = props.route.params?.params?.id;

  const notification = useSelector(profileNotificationsByIdSelector(+id || 0));
  const title = notification?.topic || 'Уведомление';
  return (
    <mainStack.Navigator initialRouteName={Screens.Main}>
      <mainStack.Screen
        name={Screens.Main}
        component={MainScreen}
        options={createRootScreenOptions(theme, ['Единая социальная карта', 'Свердловской области'], props.navigation)}
      />
      <mainStack.Screen
        name={Screens.MainNotificationsItem}
        component={NotificationMobile}
        options={createChildScreenOptions(theme, title, props.navigation, undefined, undefined, 2)}
      />
      <mainStack.Screen
        name={Screens.NewsArticle}
        component={NewsArticleScreen}
        options={createChildScreenOptions(theme, 'Последние новости', props.navigation)}
      />
      <mainStack.Screen
        name={Screens.ChangeCityMobile}
        component={ChangeCityMobileScreen}
        options={createChildScreenOptions(theme, 'Ваш город', props.navigation)}
      />
      <mainStack.Screen
        name={Screens.ProfileLinkedAccounts}
        component={ProfileLinkedAccounts}
        options={createChildScreenOptions(theme, 'Связанные аккаунты', props.navigation)}
      />
      <mainStack.Screen
        name={Screens.ProfileLinkedAccountsItem}
        component={ProfileLinkedAccountsItem}
        options={createChildScreenOptions(theme, 'Аккаунт', props.navigation)}
      />
      <mainStack.Screen
        name={Screens.EsiaActivateCardAfterLogged}
        component={esiaActivateCardAfterLoggedOnMobile}
        options={createChildScreenOptions(theme, 'Активировать карту', props.navigation)}
      />
    </mainStack.Navigator>
  );
};

export const VisuallyImpairedSettings = (props: StackScreenProps<any>) => {
  const theme = useTheme();
  return (
    <visuallyImpairedSettingsStack.Navigator>
      <visuallyImpairedSettingsStack.Screen
        name={Screens.VisuallyImpairedSettings}
        component={VisuallyImpairedSettingsScreen}
        options={createRootScreenOptions(theme, 'Настройки для слабовидящих', props.navigation)}
      />
    </visuallyImpairedSettingsStack.Navigator>
  );
};

export const Polls = (props: StackScreenProps<any>) => {
  const theme = useTheme();
  return (
    <pollsServiceStack.Navigator screenOptions={{animationEnabled: false}} initialRouteName={Screens.Polls}>
      <pollsServiceStack.Screen
        name={Screens.Polls}
        component={PollsMainScreen}
        options={createRootScreenOptions(theme, 'Опросы и голосования', props.navigation)}
      />
      <pollsServiceStack.Screen
        name={Screens.PollsTypeSurvey}
        component={PollsTypeSurveyScreen}
        options={createChildScreenOptions(theme, 'Опросы', props.navigation, Screens.Polls)}
      />
      <pollsServiceStack.Screen
        name={Screens.PollsTypeVote}
        component={PollsTypeVoteScreen}
        options={createChildScreenOptions(theme, 'Голосования', props.navigation, Screens.Polls)}
      />
      <pollsServiceStack.Screen
        name={Screens.PollsTypeSurveyForm}
        component={PollDetailsResolver(PollDetailsTypeResolver(PollsTypeSurveyFormScreen))}
        options={(navProps) =>
          createChildScreenOptions(theme, navProps.navigation.title, props.navigation, Screens.PollsTypeSurvey)
        }
      />
      <pollsServiceStack.Screen
        name={Screens.PollsTypeVoteForm}
        component={PollDetailsResolver(PollDetailsTypeResolver(PollsTypeVoteFormScreen))}
        options={(navProps) =>
          createChildScreenOptions(theme, navProps.navigation.title, props.navigation, Screens.PollsTypeVote)
        }
      />
    </pollsServiceStack.Navigator>
  );
};

export const CardServices = (props: StackScreenProps<any>) => {
  const theme = useTheme();
  return (
    <cardServiceStack.Navigator>
      <cardServiceStack.Screen
        name={Screens.CardServices}
        component={CardServicesScreen}
        options={createRootScreenOptions(theme, 'Возможности карты', props.navigation)}
      />
      <cardServiceStack.Screen
        name={Screens.TransportService}
        component={TransportServicesScreen}
        options={createChildScreenOptions(theme, 'Транспортное приложение', props.navigation, Screens.CardServices)}
      />
      <cardServiceStack.Screen
        name={Screens.BankingService}
        component={BankServicesScreen}
        options={createChildScreenOptions(theme, 'Банковские сервисы', props.navigation, Screens.CardServices)}
      />
      <cardServiceStack.Screen
        name={Screens.PrivilegesService}
        component={PrivilegesResolver(PrivilegesScreen)}
        options={createChildScreenOptions(theme, 'Мои льготы', props.navigation, Screens.CardServices)}
      />
      <cardServiceStack.Screen
        name={Screens.PrivilegesServiceDetails}
        component={PrivilegesResolver(PrivilegeDetailsScreen)}
        options={createChildScreenOptions(theme, 'Льгота', props.navigation)}
      />
      <cardServiceStack.Screen
        name={Screens.LoyaltyProgram}
        component={LoyaltyProgramScreen}
        options={createChildScreenOptions(theme, 'Скидки и бонусы', props.navigation, Screens.CardServices)}
      />
      <cardServiceStack.Screen
        name={Screens.LoyaltyProgramAcceptor}
        component={LoyaltyProgramAcceptorScreen}
        options={(navProps) => createChildScreenOptions(theme, navProps.navigation.title, props.navigation)}
      />
      <cardServiceStack.Screen
        name={Screens.AcceptorsFilter}
        component={acceptorsFilterScreen as any}
        options={createChildScreenOptions(theme, 'Категория', props.navigation)}
      />
      <cardServiceStack.Screen
        name={Screens.VolunteersService}
        component={VolunteersServiceScreen}
        options={createChildScreenOptions(theme, 'Волонтерство', props.navigation, Screens.CardServices)}
      />
      <cardServiceStack.Screen
        name={Screens.VolunteersServiceExchangeHistory}
        component={VolunteersServiceExchangeHistoryScreen}
        options={createChildScreenOptions(theme, 'История операций', props.navigation, Screens.VolunteersService)}
      />
      <cardServiceStack.Screen
        name={Screens.VolunteersServiceEvents}
        component={VolunteersServiceEventsScreen}
        options={createChildScreenOptions(theme, 'Мои мероприятия', props.navigation)}
      />
      <cardServiceStack.Screen
        name={Screens.RewardShop}
        component={RewardShop}
        options={createChildScreenOptions(theme, 'Магазин Поощрений', props.navigation)}
      />
      <cardServiceStack.Screen
        name={Screens.ProductItem}
        component={ProductItemScreen}
        options={(navProps) => createChildScreenOptions(theme, navProps.navigation.title, props.navigation)}
      />
      <cardServiceStack.Screen
        name={Screens.RewardShopFilters}
        component={FiltersViewMobile}
        options={createChildScreenOptions(theme, 'Фильтры', props.navigation, Screens.RewardShop)}
      />
      <cardServiceStack.Screen
        name={Screens.Cashback}
        component={CashBackProgramScreen}
        options={createChildScreenOptions(
          theme,
          'Регистрация в акциях Программы лояльности АО “НСПК”',
          props.navigation,
          undefined,
          undefined,
          2,
        )}
      />
      <cardServiceStack.Screen
        name={Screens.ECertificates}
        component={ECertificatesScreen}
        options={createChildScreenOptions(theme, 'Электронные сертификаты', props.navigation, undefined, undefined, 2)}
      />
      <cardServiceStack.Screen
        name={Screens.CertificateItem}
        component={CertificateItemScreen}
        options={(navProps) => {
          console.log(navProps);
          return createChildScreenOptions(theme, navProps.navigation.title, props.navigation);
        }}
      />
    </cardServiceStack.Navigator>
  );
};

export const Notifications = (props: StackScreenProps<any>) => {
  const theme = useTheme();
  const id = props.route.params?.params?.id;
  const notification = useSelector(profileNotificationsByIdSelector(+id || 0));
  const title = notification?.topic || 'Уведомление';

  return (
    <notificationsStack.Navigator>
      <notificationsStack.Screen
        name={Screens.Notifications}
        component={NotificationsScreen}
        options={createRootScreenOptions(theme, 'Уведомления', props.navigation)}
      />
      <notificationsStack.Screen
        name={Screens.NotificationsItem}
        component={NotificationMobile}
        options={createChildScreenOptions(theme, title, props.navigation, Screens.Notifications, undefined, 2)}
      />
    </notificationsStack.Navigator>
  );
};

const SocialSupportMeasures = (props: StackScreenProps<any>) => {
  const theme = useTheme();
  return (
    <cardServiceStack.Navigator>
      <cardServiceStack.Screen
        name={Screens.SocialSupportMeasures}
        component={SocialSupportMeasuresScreen}
        options={createRootScreenOptions(theme, 'Меры социальной поддержки', props.navigation)}
      />
      <cardServiceStack.Screen
        name={Screens.StatePrograms}
        component={StateProgramsScreen}
        options={createChildScreenOptions(
          theme,
          'Государственные программы',
          props.navigation,
          Screens.SocialSupportMeasures,
        )}
      />
      <cardServiceStack.Screen
        name={Screens.StateAssignments}
        component={StateAssignmentsScreen}
        options={createChildScreenOptions(
          theme,
          'Государственные задания',
          props.navigation,
          Screens.SocialSupportMeasures,
        )}
      />
      <cardServiceStack.Screen
        name={Screens.ExpenseLiabilities}
        component={ExpenseLiabilitiesScreen}
        options={createChildScreenOptions(
          theme,
          'Реестр расходных обязательств',
          props.navigation,
          Screens.SocialSupportMeasures,
        )}
      />
    </cardServiceStack.Navigator>
  );
};

const SupportService = (props: StackScreenProps<any>) => {
  const theme = useTheme();
  const request = useSelector(profileCurrentRequestSelector());
  const styles = useStyles(() => {
    return {
      plusContainer: {
        color: theme.color.labelText,
      },
      plusColor: {
        color: theme.color.labelText,
      },
    };
  });
  const applicationTitle = request
    ? `Обращение №${request?.number} от ${datePipe(request.dateTime, dateFormats.fullDate)}`
    : 'Обращение';

  const SupportServiceCreateBtn = React.useCallback(() => {
    return (
      <TouchableOpacity
        testID='support-service-create-btn-id'
        onPress={() => {
          props.navigation.navigate(Screens.SupportServiceCreateTopic);
        }}>
        <View style={styles.plusContainer as any}>
          <images.PlusInCircle color={styles.plusColor.color} height={36} width={36} />
        </View>
      </TouchableOpacity>
    );
  }, [props.navigation]);

  return (
    <cardServiceStack.Navigator initialRouteName={Screens.SupportService}>
      <cardServiceStack.Screen
        name={Screens.SupportService}
        component={SupportServiceScreen}
        options={createRootScreenOptions(theme, 'Служба поддержки', props.navigation)}
      />
      <cardServiceStack.Screen
        name={Screens.SupportServiceTopics}
        component={SupportServiceTopics}
        options={createChildScreenOptions(
          theme,
          'Мои обращения',
          props.navigation,
          Screens.SupportService,
          SupportServiceCreateBtn,
        )}
      />
      <cardServiceStack.Screen
        name={Screens.SupportServiceCreateTopic}
        component={CreateRequest}
        options={createChildScreenOptions(theme, 'Новое обращение', props.navigation, Screens.SupportServiceTopics)}
      />
      <cardServiceStack.Screen
        name={Screens.SupportServiceTopic}
        component={SupportServiceTopic}
        options={createChildScreenOptions(
          theme,
          applicationTitle,
          props.navigation,
          Screens.SupportServiceTopics,
          undefined,
          2,
        )}
      />
    </cardServiceStack.Navigator>
  );
};

const AboutAppScreen = (props: StackScreenProps<any>) => {
  const theme = useTheme();
  return (
    <cardServiceStack.Navigator initialRouteName={Screens.AboutApplication}>
      <cardServiceStack.Screen
        name={Screens.AboutApplication}
        component={AboutApplicationScreen}
        options={createRootScreenOptions(theme, 'О приложении', props.navigation)}
      />
      <cardServiceStack.Screen
        name={Screens.SupportServiceCreateTopic}
        component={CreateRequest}
        options={createChildScreenOptions(theme, 'Новое обращение', props.navigation)}
      />
      <cardServiceStack.Screen
        name={Screens.SupportServiceTopic}
        component={SupportServiceTopic}
        options={createChildScreenOptions(theme, 'Обращение', props.navigation)}
      />
    </cardServiceStack.Navigator>
  );
};

export const News = (props: StackScreenProps<any>) => {
  const theme = useTheme();
  return (
    <newsStack.Navigator initialRouteName={Screens.News}>
      <newsStack.Screen
        name={Screens.News}
        component={NewsScreen}
        options={createRootScreenOptions(theme, 'Новости', props.navigation)}
      />
      <newsStack.Screen
        name={Screens.NewsArticle}
        component={NewsArticleScreen}
        options={createChildScreenOptions(theme, 'Последние новости', props.navigation)}
      />
    </newsStack.Navigator>
  );
};
const Plug = () => null;

const bottomTabBarItems = [
  {
    route: Screens.Main,
    webRoute: Routes.mainMobile,
    component: Main,
    Icon: images.TabBarHome,
    label: 'Главная',
  },
  {
    route: Screens.CardServicesMenu,
    webRoute: Routes.cardService,
    Icon: images.TabBarPossibilities,
    label: 'Возможности',
    component: CardServices,
  },
  {
    route: Screens.QrCodeCard,
    webRoute: Routes.qrCodeCard,
    Icon: images.TabBarCard,
    component: Plug,
    isCenter: true,
    label: 'Карта',
  },
  {
    route: Screens.SupportServiceTab,
    webRoute: Routes.supportService,
    component: SupportService,
    Icon: images.TabBarSupport,
    label: 'Поддержка',
  },
  {
    route: Screens.DrawerMenu,
    webRoute: Routes.drawer,
    Icon: images.TabBarMenu,
    component: Plug,
    label: 'Меню',
  },
  {
    route: Screens.PollsMenu,
    webRoute: Routes.polls,
    component: PollsResolver(Polls),
    Icon: images.TabBarSupport,
    label: 'Поддержка',
  },
  // next route render in bottom bar but their position behind the screen
  {
    route: Screens.WebView,
    webRoute: Routes.webView,
    component: WebScreen,
    Icon: images.Info,
  },
  {
    route: Screens.AboutApplication,
    webRoute: Routes.aboutApplication,
    component: AboutAppScreen,
    Icon: images.Info,
  },
  {
    route: Screens.VisuallyImpairedSettings,
    webRoute: Routes.visuallyImpairedSettings,
    component: VisuallyImpairedSettings,
    Icon: images.Info,
  },
  {
    route: Screens.NotificationsMenu,
    webRoute: Routes.mobileNotifications,
    component: Notifications,
    Icon: images.Info,
  },
  {
    route: Screens.SocialSupportMeasuresMenu,
    webRoute: Routes.socialSupportMeasures,
    component: SocialSupportMeasures,
    Icon: images.Info,
  },
  {
    route: Screens.Profile,
    webRoute: Routes.profile,
    Icon: images.Profile,
    component: MobileProfileNavigation,
  },
  {
    route: Screens.News,
    webRoute: Routes.news,
    Icon: images.Info,
    component: News,
  },
  {
    route: Screens.SmsEmailSubscriptionControlScreen,
    webRoute: Routes.smsEmailSubscriptionControlPath,
    Icon: images.Info,
    component: SmsEmailSubscriptionControlScreen,
  },
  {
    route: Screens.EsiaActivateCard,
    webRoute: Routes.esiaActivateCard,
    component: esiaActivateCard,
    Icon: images.Info,
  },
];

const TabBarIcon: React.FC<TabBarIconProps & {isCenter?: boolean; isDisabled?: boolean; label?: string}> = (props) => {
  const Icon = props.Icon;
  const _theme = useTheme();
  const styles = useStyles(({theme}) => ({
    iconContainer: {
      // height: 56
      height: 49,
      width: 80,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      paddingTop: 5,
      // paddintTop: 12
    },
    iconContainerRound: {
      // width: 58,
      // height: 58,
      // borderRadius: 29,
      // marginTop: -34,
      top: -20,
      paddingTop: 0,
    },
    iconWrapper: {
      width: 32,
      height: 32,
      alignItems: 'center',
      justifyContent: 'center',
    },
    iconGradient: {
      position: 'absolute',
      left: -13,
      borderRadius: 29,
      right: 0,
      width: 58,
      height: 58,
      backgroundColor: theme.color.primaryBlackColor,
    },
    linkIcon: {
      color: theme.bottomMenu.linkIconColor,
    },
    activeLinkIcon: {
      color: theme.bottomMenu.activeLinkIconColor,
    },
    label: {
      ...theme.fonts.small,
      textAlign: 'center',
    },
  }));

  const {isCenter} = props;
  const color = props.focused ? _theme.bottomMenu.activeLinkIconColor : _theme.bottomMenu.linkIconColor;
  const centerLinkIconGradient = props.isDisabled
    ? [_theme.color.iconGrayColor, _theme.color.iconGrayColor]
    : _theme.bottomMenu.centerLinkIconGradient;

  return (
    <View testID={`tab-bar-${props.routeName}-container-id`} style={styles.iconContainer}>
      <View style={[styles.iconWrapper, isCenter ? styles.iconContainerRound : {}]}>
        {isCenter ? (
          <GradientView
            style={styles.iconGradient}
            gradient={{
              angle: 90,
              colors: centerLinkIconGradient.map((linkColor) => hexToRgbA(linkColor, 1)),
            }}
          />
        ) : null}
        <Icon
          width={isCenter ? 24 : 32}
          height={isCenter ? 24 : 32}
          color={isCenter ? _theme.bottomMenu.centerLinkIconColor : color}
        />
      </View>
      <Text numberOfLines={1} style={[styles.label, {color}]}>
        {props.label}
      </Text>
    </View>
  );
};

const routesWithHiddenTabBar = [Screens.ProfileUserEmailConfirmation, Screens.ProfileUserPhoneConfirmation];

function isTabBarVisibile(route: any): boolean {
  const routeName: any = getFocusedRouteNameFromRoute(route);
  return !routesWithHiddenTabBar.includes(routeName);
}

const nonVisibleTabs = [
  Screens.PollsMenu,
  Screens.VisuallyImpairedSettings,
  Screens.AboutApplication,
  Screens.WebView,
  Screens.Notifications,
  Screens.SocialSupportMeasures,
  Screens.EsiaActivateCard,
  Screens.SmsEmailSubscriptionControlScreen,
];

export const BottomStackNavigator: React.FC = () => {
  const token = useSelector(profileTokenSelector());
  const {cardStatusId} = (useSelector(holderProfileSelector()) as GetHolderProfileCabinetResponsePayload) || {
    cardStatusId: null,
  };
  const {width} = useDimensions();
  const modal = useModal();

  if (Platform.OS === 'web') {
    return (
      <>
        <GoToApp />
        <MobileStackNavigation.Navigator initialRouteName={Screens.Main} headerMode='none'>
          {bottomTabBarItems.map(({route, component}) => (
            <MobileStackNavigation.Screen
              name={route}
              component={component}
              key={route}
              options={{
                title: '',
              }}
            />
          ))}
        </MobileStackNavigation.Navigator>
      </>
    );
  }
  return (
    <Tabs.Navigator
      initialRouteName={Screens.Main}
      tabBar={(props) => {
        return (
          <TabBarCustom
            customPressHandlers={{
              ...(Platform.OS !== 'web'
                ? {
                    [Screens.DrawerMenu]: () => {
                      props.navigation.dispatch(DrawerActions.toggleDrawer);
                    },
                    [Screens.QrCodeCard]: () => {
                      if (cardStatusId !== CardStatus.Blocked) {
                        modal.show({
                          render: ({id}, methods) => <CardCodeScreen onClose={() => methods.remove(id)} />,
                          mobile: true,
                        });
                      }
                    },
                  }
                : {}),
            }}
            {...props}
            uscIsBlocked={cardStatusId === CardStatus.Blocked}
            tabStyle={{
              width: width / defaults.sizeOfBottomBar,
              ...Platform.select({
                ios: {flex: 0},
                android: {flex: 0},
                web: {
                  minWidth: width / defaults.sizeOfBottomBar,
                },
              }),
            }}
            state={{...props.state, routes: props.state.routes}}
          />
        );
      }}
      screenOptions={({route}) => ({tabBarVisible: !!token && isTabBarVisibile(route)})}>
      {bottomTabBarItems.map(({route, label, component, Icon, webRoute, isCenter}) => (
        <Tabs.Screen
          name={route}
          component={component}
          key={route}
          options={{
            tabBarIcon: (tabBarIconProps) => {
              if (!nonVisibleTabs.includes(route)) {
                return (
                  <TabBarIcon
                    {...tabBarIconProps}
                    webRoute={webRoute}
                    Icon={Icon}
                    isCenter={isCenter}
                    isDisabled={isCenter && cardStatusId === CardStatus.Blocked}
                    routeName={route}
                    label={label}
                  />
                );
              }
              return null;
            },
          }}
        />
      ))}
    </Tabs.Navigator>
  );
};
